import { EVENTS } from '@/plugins/analytics/events';
import { isFocusedSyntheticObservation } from '@shared/assessment-definition/dist/src/focused-plan';
import { track } from '@/plugins/analytics';
import { setActiveTextModeContext } from './text-mode-context';
import type { TextModeAnalyticsContext } from './text-mode-context';
export { setActiveTextModeContext, getActiveTextModeContext, clearActiveTextModeContext } from './text-mode-context';
export type { FlexiAssessmentMode, TextModeAnalyticsContext } from './text-mode-context';
import type {
  AuditEntry,
  EvidenceCategory,
  QuestionItem,
  TextModeAlertState,
  TextModeExtraction,
  TextModeObservation,
} from '../types/text-mode.types';
import { resolveTextModeAlertType } from '../stores/alert.store';
export { resolveTextModeAlertType } from '../stores/alert.store';

const TEXT_MODE_PANEL_NAME = 'text-mode';

type SubmissionType = 'first_submit' | 'resubmit';
type TextModeAlertTriggerAction = 'note_submit' | 'question_submit' | 'rhs_submit';
type TextModeAlertAction = 'back' | 'continue';

interface TimedTextModeAnalyticsContext extends TextModeAnalyticsContext {
  startedAt?: number | null;
  firstSubmittedAt?: number | null;
}

interface SubmitPayloadParams extends TextModeAnalyticsContext {
  rawText: string;
  submissionIndex: number;
  submissionType: SubmissionType;
  questions: QuestionItem[];
  evidenceCategories: EvidenceCategory[];
  extractions: TextModeExtraction[];
  observations: TextModeObservation[];
  focusedAssessments: string[];
}

interface CompletePayloadParams extends TimedTextModeAnalyticsContext {
  submissionCount: number;
  rhsEditSubmitCount: number;
}

interface RhsEditPayloadParams extends TextModeAnalyticsContext {
  rhsEditSubmitCount: number;
  auditEntries: AuditEntry[];
  evidenceCategories: EvidenceCategory[];
}

interface QuestionAnsweredPayloadParams extends TextModeAnalyticsContext {
  questionRoundIndex: number;
  question: QuestionItem | null;
}

interface QuestionsSubmittedPayloadParams extends TextModeAnalyticsContext {
  questionRoundIndex: number;
  questionSubmitCount: number;
  questions: QuestionItem[];
  returnedQuestions: QuestionItem[];
  observations: TextModeObservation[];
  focusedAssessments: string[];
  comprehensive: boolean;
}

interface AlertPayloadParams extends TextModeAnalyticsContext {
  alertState: TextModeAlertState;
  triggerAction: TextModeAlertTriggerAction;
  questions: QuestionItem[];
  evidenceCategories: EvidenceCategory[];
  submissionIndex?: number;
  submissionType?: SubmissionType;
}

interface AlertActionPayloadParams extends TextModeAnalyticsContext {
  alertState: TextModeAlertState;
  action: TextModeAlertAction;
}

interface NetworkErrorPayloadParams extends TextModeAnalyticsContext {
  actionType: TextModeAlertTriggerAction;
}

interface TextModeAnalyticsTestGlobal {
  __textModeAnalyticsEvents?: Array<{
    event: string;
    properties: Record<string, unknown>;
  }>;
}

function secondsSince(startedAt?: number | null): number | null {
  if (!startedAt) return null;
  return Math.max(0, Math.round((Date.now() - startedAt) / 1000));
}

function basePayload(context: TextModeAnalyticsContext): Record<string, unknown> {
  return {
    encounter_id: context.encounterId,
    panel_name: TEXT_MODE_PANEL_NAME,
    route_name: context.routeName || null,
    event_timestamp: new Date().toISOString(),
  };
}

function emitTextModeEvent(event: string, payload: Record<string, unknown>): void {
  const testGlobal = globalThis as typeof globalThis & TextModeAnalyticsTestGlobal;
  if (Array.isArray(testGlobal.__textModeAnalyticsEvents)) {
    testGlobal.__textModeAnalyticsEvents.push({ event, properties: payload });
  }

  track(event, payload);
}

function hasMappedValue(item: EvidenceCategory['items'][number]): boolean {
  if (item.observationStatus) return true;
  if (item.value === null || item.value === undefined) return false;
  return String(item.value).trim().length > 0;
}

function countEvidenceItems(evidenceCategories: EvidenceCategory[]): number {
  return evidenceCategories.reduce((sum, category) => sum + category.items.length, 0);
}

function countMappedStructuredVariables(evidenceCategories: EvidenceCategory[]): number {
  return evidenceCategories.reduce(
    (sum, category) => sum + category.items.filter(hasMappedValue).length,
    0,
  );
}

function summarizeQuestions(questions: QuestionItem[]): Array<Record<string, unknown>> {
  return questions.map((question) => ({
    id: question.id,
    prompt_token: question.promptToken || null,
    type: question.type,
    answered: String(question.answer || '').trim().length > 0,
    option_tokens: question.type === 'option'
      ? (question.responses || []).map((response) => response.token)
      : [],
  }));
}

function summarizeExtractions(extractions: TextModeExtraction[]): Array<Record<string, unknown>> {
  return extractions.map((extraction) => ({
    name: extraction.name,
    confidence: extraction.confidence,
    extraction_id: extraction.extractionID || null,
  }));
}

function summarizeObservations(observations: TextModeObservation[]): Array<Record<string, unknown>> {
  return observations.filter(observation => !isFocusedSyntheticObservation(observation.name)).map((observation) => ({
    name: observation.name,
    type: observation.type,
    category: observation.category,
    source: observation.source || null,
  }));
}

function summarizeDisplayedObservations(evidenceCategories: EvidenceCategory[]): Array<Record<string, unknown>> {
  return evidenceCategories.flatMap((category) =>
    category.items.map((item) => ({
      item_id: item.id,
      name: item.name,
      name_token: item.nameToken || null,
      observation_status: item.observationStatus || null,
      category_id: category.id,
      category_title: category.title,
      source: item.source || null,
      structured: Boolean(item.structured),
    })),
  );
}

function summarizeAnsweredObservation(
  question: QuestionItem | null,
): Record<string, unknown> | null {
  if (!question || question.attributeOf) return null;

  return {
    name: question.id,
    type: question.type,
    source: 'response',
  };
}

function findEvidenceItem(
  evidenceCategories: EvidenceCategory[],
  itemId: string,
): { category: EvidenceCategory; item: EvidenceCategory['items'][number] } | null {
  for (const category of evidenceCategories) {
    const item = category.items.find((candidate) => candidate.id === itemId);
    if (item) {
      return { category, item };
    }
  }

  return null;
}

function buildAlertPayload(params: AlertPayloadParams): Record<string, unknown> | null {
  const alertType = resolveTextModeAlertType(params.alertState);
  if (!alertType) return null;

  const mappedStructuredCount = countMappedStructuredVariables(params.evidenceCategories);
  const evidenceItemCount = countEvidenceItems(params.evidenceCategories);

  return {
    ...basePayload(params),
    alert_type: alertType,
    trigger_action: params.triggerAction,
    submission_index: params.submissionIndex ?? null,
    submission_type: params.submissionType ?? null,
    mapped_structured_count: mappedStructuredCount,
    has_structured_mapping: mappedStructuredCount > 0,
    question_count: params.questions.length,
    evidence_item_count: evidenceItemCount,
  };
}

function summarizeAuditEntries(
  auditEntries: AuditEntry[],
  evidenceCategories: EvidenceCategory[],
): Array<Record<string, unknown>> {
  return auditEntries.map((entry) => {
    const evidence = findEvidenceItem(evidenceCategories, entry.itemId);

    return {
      item_id: entry.itemId,
      item_name: evidence?.item.name || null,
      item_name_token: evidence?.item.nameToken || null,
      category_id: evidence?.category.id || null,
      category_title: evidence?.category.title || null,
      field_name: entry.fieldName,
      marked_incorrect: entry.markedIncorrect,
      committed_at: entry.committedAt,
    };
  });
}

export function buildNoteSubmittedPayload(params: SubmitPayloadParams): Record<string, unknown> {
  const mappedStructuredCount = countMappedStructuredVariables(params.evidenceCategories);
  const evidenceItemCount = countEvidenceItems(params.evidenceCategories);
  const extractedObservations = summarizeObservations(params.observations);
  const displayedObservations = summarizeDisplayedObservations(params.evidenceCategories);
  const returnedQuestions = summarizeQuestions(params.questions);

  return {
    ...basePayload(params),
    submission_index: params.submissionIndex,
    submission_type: params.submissionType,
    input_text_length: params.rawText.length,
    extracted_entities: summarizeExtractions(params.extractions),
    extracted_observations: extractedObservations,
    extracted_observation_count: extractedObservations.length,
    displayed_observations: displayedObservations,
    displayed_observation_count: displayedObservations.length,
    focused_assessments: params.focusedAssessments,
    focused_assessment_count: params.focusedAssessments.length,
    returned_questions: returnedQuestions,
    returned_question_count: returnedQuestions.length,
    mapped_structured_count: mappedStructuredCount,
    has_structured_mapping: mappedStructuredCount > 0,
    question_count: params.questions.length,
    evidence_item_count: evidenceItemCount,
    validation_signals: {
      mapped_structured_count: mappedStructuredCount,
      has_structured_mapping: mappedStructuredCount > 0,
      question_count: params.questions.length,
      evidence_item_count: evidenceItemCount,
      extracted_observation_count: extractedObservations.length,
      displayed_observation_count: displayedObservations.length,
      focused_assessment_count: params.focusedAssessments.length,
    },
  };
}

function buildQuestionAnsweredPayload(
  params: QuestionAnsweredPayloadParams,
): Record<string, unknown> {
  const answeredObservation = summarizeAnsweredObservation(params.question);

  return {
    ...basePayload(params),
    question_round_index: params.questionRoundIndex,
    question_id: params.question?.id || null,
    prompt_token: params.question?.promptToken || null,
    question_type: params.question?.type || null,
    answered_observation: answeredObservation,
    extracted_observations: answeredObservation ? [answeredObservation] : [],
    extracted_observation_count: answeredObservation ? 1 : 0,
  };
}

function buildQuestionsSubmittedPayload(
  params: QuestionsSubmittedPayloadParams,
): Record<string, unknown> {
  const questions = summarizeQuestions(params.questions);
  const answeredQuestions = params.questions.filter((question) => String(question.answer || '').trim().length > 0);
  const answeredObservations = answeredQuestions
    .filter((question) => !question.attributeOf)
    .map((question) => ({
      name: question.id,
      type: question.type,
      source: 'response',
    }));
  const extractedObservations = summarizeObservations(params.observations);
  const returnedQuestions = summarizeQuestions(params.returnedQuestions);

  return {
    ...basePayload(params),
    question_round_index: params.questionRoundIndex,
    question_submit_count: params.questionSubmitCount,
    questions,
    question_count: params.questions.length,
    answered_count: answeredQuestions.length,
    unanswered_count: params.questions.length - answeredQuestions.length,
    answered_observations: answeredObservations,
    extracted_observations: extractedObservations,
    extracted_observation_count: extractedObservations.length,
    returned_questions: returnedQuestions,
    returned_question_count: params.returnedQuestions.length,
    focused_assessments: params.focusedAssessments,
    focused_assessment_count: params.focusedAssessments.length,
    comprehensive: params.comprehensive,
  };
}

function buildRhsEditSubmittedPayload(params: RhsEditPayloadParams): Record<string, unknown> {
  const markedIncorrectCount = params.auditEntries.filter((entry) => entry.markedIncorrect).length;

  return {
    ...basePayload(params),
    rhs_edit_submit_count: params.rhsEditSubmitCount,
    edited_field_count: params.auditEntries.length,
    marked_incorrect_count: markedIncorrectCount,
    audit_entries: summarizeAuditEntries(params.auditEntries, params.evidenceCategories),
  };
}

export function trackTextModeStarted(context: TextModeAnalyticsContext): void {
  setActiveTextModeContext(context);
  emitTextModeEvent(EVENTS.FLEXI_TEXT_MODE_STARTED, basePayload(context));
}

export function trackNoteSubmitted(params: SubmitPayloadParams): void {
  emitTextModeEvent(EVENTS.FLEXI_TEXT_NOTE_SUBMITTED, buildNoteSubmittedPayload(params));
}

export function trackNoteResubmitRequested(
  context: TextModeAnalyticsContext & { nextSubmissionIndex: number },
): void {
  emitTextModeEvent(EVENTS.FLEXI_TEXT_NOTE_RESUBMIT_REQUESTED, {
    ...basePayload(context),
    next_submission_index: context.nextSubmissionIndex,
  });
}

export function trackQuestionAnswered(params: QuestionAnsweredPayloadParams): void {
  emitTextModeEvent(EVENTS.FLEXI_TEXT_QUESTION_ANSWERED, buildQuestionAnsweredPayload(params));
}

export function trackQuestionsSubmitted(params: QuestionsSubmittedPayloadParams): void {
  emitTextModeEvent(EVENTS.FLEXI_TEXT_QUESTIONS_SUBMITTED, buildQuestionsSubmittedPayload(params));
}

export function trackRhsEditStarted(context: TextModeAnalyticsContext): void {
  emitTextModeEvent(EVENTS.FLEXI_TEXT_RHS_EDIT_STARTED, basePayload(context));
}

export function trackRhsEditSubmitted(params: RhsEditPayloadParams): void {
  emitTextModeEvent(EVENTS.FLEXI_TEXT_RHS_EDIT_SUBMITTED, buildRhsEditSubmittedPayload(params));
}

export function trackAssessmentCompleted(params: CompletePayloadParams): void {
  emitTextModeEvent(EVENTS.FLEXI_TEXT_ASSESSMENT_COMPLETED, {
    ...basePayload(params),
    submission_count: params.submissionCount,
    rhs_edit_submit_count: params.rhsEditSubmitCount,
    duration_seconds_from_text_start: secondsSince(params.startedAt),
    duration_seconds_from_first_submit: secondsSince(params.firstSubmittedAt),
  });
}

export function trackFinalizeSaveClicked(context: TextModeAnalyticsContext): void {
  if (context.mode === 'guided') {
    emitTextModeEvent(EVENTS.FLEXI_GUIDED_FINALIZE_SAVE_CLICKED, {
      encounter_id: context.encounterId,
      route_name: context.routeName || null,
      event_timestamp: new Date().toISOString(),
    });
    return;
  }

  emitTextModeEvent(EVENTS.FLEXI_TEXT_FINALIZE_SAVE_CLICKED, basePayload(context));
}

export function trackTextModeAlertShown(params: AlertPayloadParams): void {
  const payload = buildAlertPayload(params);
  if (!payload) return;

  emitTextModeEvent(EVENTS.FLEXI_TEXT_ALERT_SHOWN, payload);

  if (payload.alert_type === 'not_clinical') {
    emitTextModeEvent(EVENTS.FLEXI_TEXT_EXTRACTION_NO_MAPPING, payload);
  }
}

export function trackTextModeComprehensiveEscalationPrompted(
  params: AlertPayloadParams,
): void {
  const payload = buildAlertPayload({
    ...params,
    alertState: {
      isComprehensive: true,
      isNotClinicalConcept: false,
      isNoFocusedLogic: false,
    },
  });
  if (!payload) return;

  emitTextModeEvent(EVENTS.FLEXI_TEXT_FOCUSED_TO_COMPREHENSIVE_PROMPTED, payload);
}

export function trackTextModeAlertActionClicked(params: AlertActionPayloadParams): void {
  const alertType = resolveTextModeAlertType(params.alertState);
  if (!alertType) return;

  emitTextModeEvent(EVENTS.FLEXI_TEXT_ALERT_ACTION_CLICKED, {
    ...basePayload(params),
    alert_type: alertType,
    action: params.action,
  });
}

export function trackTextModeNetworkErrorShown(params: NetworkErrorPayloadParams): void {
  emitTextModeEvent(EVENTS.FLEXI_TEXT_NETWORK_ERROR_SHOWN, {
    ...basePayload(params),
    action_type: params.actionType,
  });
}

export function trackTextModeNetworkErrorRetryClicked(params: NetworkErrorPayloadParams): void {
  emitTextModeEvent(EVENTS.FLEXI_TEXT_NETWORK_ERROR_RETRY_CLICKED, {
    ...basePayload(params),
    action_type: params.actionType,
  });
}
